import { mapActions, mapGetters, mapState } from 'vuex';
import accountAddressUpdate from '@/components/account/account-details/update/account-address-update.vue';
import accountDetailsUpdate from '@/components/account/account-details/update/account-details-update.vue';
import accountUpdatePassword from '@/components/account/account-update-password/account-update-password.vue';
import accountSetPassword from '@/components/account/account-set-password/account-set-password.vue';
import contentHeader from '@/components/generics/content-header/content-header.vue';
import profile from '@/components/account/profile/profile.vue';
import accountVerifyMixin from '@/mixins/accountVerify';
import { MODALS } from '@/components/app/app-modals/modals-constants';
import notificationIcon from '@/components/notification-icon/notification-icon.vue';
import contentLoaderMixin from '@/mixins/contentLoader';
import loaderMixin from '@/mixins/loader';
import accountDesktopHeader from '@/components/account/account-desktop-header/account-desktop-header.vue';

const ROUTE_DETAILS = 'account-details';
const ROUTE_DETAILS_UPDATE = 'details-update';
const ROUTE_ADDRESS_UPDATE = 'address-update';
const ROUTE_PASS_UPDATE = 'password-update';
const ROUTE_PASS_SET = 'password-set';

const STATUS_NOT_VERIFIED = 0;
const STATUS_BASIC = 1;
const STATUS_IN_PROCESS = 2;
const STATUS_ENHANCED = 3;
const UPLOAD_LINK_SOURCE = 3;

const VERIFICATION_STATUS_MAP = {
  [STATUS_NOT_VERIFIED]: 'Not Verified',
  [STATUS_BASIC]: 'Basic',
  [STATUS_IN_PROCESS]: 'In Process',
  [STATUS_ENHANCED]: 'Enhanced',
};

export default {
  mixins: [
    accountVerifyMixin,
    contentLoaderMixin,
    loaderMixin,
  ],
  components: {
    accountDesktopHeader,
    accountAddressUpdate,
    accountDetailsUpdate,
    accountUpdatePassword,
    accountSetPassword,
    contentHeader,
    profile,
    notificationIcon,
  },
  data() {
    return {
      hasLocalPassword: false,
      documentUploadLink: '',
      STATUS_NOT_VERIFIED,
      STATUS_BASIC,
      STATUS_IN_PROCESS,
      STATUS_ENHANCED,
    };
  },
  metaInfo() {
    return {
      title: 'Account Details',
    };
  },
  watch: {
    $route(v) {
      this.triggerPopup(this.isLayoutMobile, v.name);
    },
    isLayoutMobile() {
      this.triggerPopup(this.isLayoutMobile, this.$route.name);
    },
  },
  computed: {
    ...mapState('popup', ['currentPopup']),
    ...mapGetters('app', ['isLayoutMobile', 'collegeAffiliationEnabled']),
    ...mapState('profile', ['userAffiliations', 'isUCAffilSet']),
    ...mapState('user', ['userData', 'status']),
    gridRowModifier() {
      return {
        row: this.isLayoutMobile,
        'row-x': !this.isLayoutMobile,
      };
    },
    isDetailsShown() {
      return !this.isLayoutMobile || this.$route.name === ROUTE_DETAILS;
    },
    isMobileAddressUpdate() {
      return this.isLayoutMobile && (this.$route.name === ROUTE_ADDRESS_UPDATE);
    },
    isMobileDetailsUpdate() {
      return this.isLayoutMobile && (this.$route.name === ROUTE_DETAILS_UPDATE);
    },
    isMobilePasswordChange() {
      return this.isLayoutMobile && (this.$route.name === ROUTE_PASS_UPDATE);
    },
    isMobilePasswordSet() {
      return this.isLayoutMobile && (this.$route.name === ROUTE_PASS_SET);
    },
    isUpdateAddressDisabled() {
      const idVerified = this.userData.idVerified;
      return (typeof idVerified !== 'undefined') ? !idVerified : false;
    },
    title() {
      if (this.isMobilePasswordChange) return 'Change Password';
      if (this.isMobilePasswordSet) return 'Set Password';
      if (this.isMobileAddressUpdate) return 'Change Address';
      if (this.isMobileDetailsUpdate) return 'Change Username';
      return 'Account Details';
    },
    verificationStatusDisplay() {
      const currentStatus = this.userData.verificationStatus;
      return VERIFICATION_STATUS_MAP[currentStatus];
    },
    showVerificationStatusInfo() {
      const currentStatus = this.userData.verificationStatus;
      return currentStatus !== STATUS_ENHANCED;
    },
    contentHeaderButtonSectionModifiers() {
      return {
        'content-header__section-alt--right': true,
        'content-header__section-alt--right--large-pad': !this.verificationStatusIs(STATUS_ENHANCED)
          && !this.verificationStatusIs(STATUS_IN_PROCESS),
      };
    },
  },
  methods: {
    ...mapActions('profile', ['getUserAffiliations']),
    ...mapActions('popup', ['setCurrentPopup', 'setCurrentSmallPopup', 'setCurrentPopupOptions']),
    ...mapActions('user', ['getAccountDetail', 'getManageInfo', 'getDocumentUploadLink']),
    onInfoClick() {
      this.setCurrentSmallPopup(MODALS.SMALL_PUBLIC_PROFILE_INFO);
    },
    verificationStatusIs(target) {
      return this.userData.verificationStatus === target;
    },
    onVerifyIdentity() {
      if (!this.hasUserClassicEnabled) {
        this.setCurrentPopup(MODALS.ACCOUNT_ENABLE_CLASSIC);
        return;
      }
      this.showAccountVerifyPopup();
    },
    triggerPopup(mobile, $route) {
      if (!this.hasUserClassicEnabled) {
        this.setCurrentPopup(MODALS.ACCOUNT_ENABLE_CLASSIC);
        return;
      }
      if (!mobile) {
        if ($route === ROUTE_ADDRESS_UPDATE) {
          this.setCurrentPopup(MODALS.ACCOUNT_ADDRESS_UPDATE);
        } else if ($route === ROUTE_DETAILS_UPDATE) {
          this.setCurrentPopup(MODALS.ACCOUNT_DETAIL_UPDATE);
        } else if ($route === ROUTE_PASS_UPDATE) {
          this.setCurrentPopup(MODALS.ACCOUNT_DETAIL_PASSWORD);
        } else if ($route === ROUTE_PASS_SET) {
          this.setCurrentPopup(MODALS.ACCOUNT_DETAIL_PASSWORD_SET);
        } else {
          this.setCurrentPopup();
        }
      } else {
        this.setCurrentPopup();
      }
    },
    onShowVerificationInfo() {
      this.setCurrentSmallPopup(MODALS.SMALL_VERIFICATION_INFO);
      this.setCurrentPopupOptions({ verificationStatus: this.userData.verificationStatus });
    },
    onUploadDocuments() {
      this.getDocumentUploadLink(UPLOAD_LINK_SOURCE)
        .then((link) => {
          window.location.href = link;
        });
    },
    showAffiliationsPopup() {
      if (!this.hasUserClassicEnabled) {
        this.setCurrentPopup(MODALS.ACCOUNT_ENABLE_CLASSIC);
        return;
      }
      this.setCurrentPopup(MODALS.COLLEGE_AFFILIATION);
      this.setCurrentPopupOptions({ allowClose: true });
    },
    showPhoneUpdatePopup() {
      if (!this.hasUserClassicEnabled) {
        this.setCurrentPopup(MODALS.ACCOUNT_ENABLE_CLASSIC);
        return;
      }
      this.setCurrentPopup(MODALS.PHONE_UPDATE);
    },
  },
  created() {
    this.loaderAdd(document.body);
    this.getAccountDetail()
      .then(() => (this.collegeAffiliationEnabled ? this.getUserAffiliations() : null))
      .then(this.getManageInfo)
      .then((data) => {
        this.loaderRemove(document.body);
        this.hasLocalPassword = data.hasLocalPassword;
        this.isComponentReady = true;
      })
      .catch(() => {
        this.loaderRemove(document.body);
        this.isComponentReady = true;
      });

    this.triggerPopup(this.isLayoutMobile, this.$route.name);
  },
};



// WEBPACK FOOTER //
// ./src/components/account/account-details/account-details.js